import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Modal, LabelPreview, StepBanner } from '../components/UI';

// ============ LOTES ============
export function Lotes() {
  const [lotes, setLotes] = useState([]);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [etiqueta, setEtiqueta] = useState(null);

  const load = async () => {
    const data = await api.getLotes(search, estado);
    setLotes(data);
  };
  useEffect(() => { load(); }, [search, estado]);

  const estadoBadge = (est) => ({
    Activo: <span className="badge badge-green">✓ Activo</span>,
    'Por vencer': <span className="badge badge-amber">⚠ Por vencer</span>,
    Vencido: <span className="badge badge-red">✗ Vencido</span>,
  }[est] || <span className="badge badge-gray">—</span>);

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Lotes en bodega</div>
          <div className="topbar-meta">Gestión, consulta y etiquetado de lotes activos</div>
        </div>
      </div>
      <StepBanner
        step={2} title="Lotes en bodega"
        desc="Cada recepción crea un lote. Aquí puedes consultar su estado, fecha de vencimiento, stock y generar etiquetas para identificación física."
        prev="Recepción MP" next="Stock disponible"
      />
      <div className="page-content">
        <div className="filter-bar">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por lote o producto..." />
          <select value={estado} onChange={e => setEstado(e.target.value)} style={{ width: 180 }}>
            <option value="">Todos los estados</option>
            <option>Activo</option>
            <option>Por vencer</option>
            <option>Vencido</option>
          </select>
        </div>
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Lote</th><th>Producto</th><th>Ingreso</th><th>Vencimiento</th><th>Stock</th><th>Proveedor</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {lotes.length === 0
                  ? <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Sin lotes disponibles</td></tr>
                  : lotes.map(r => (
                    <tr key={r.id}>
                      <td><span className="lot-tag">{r.lote}</span></td>
                      <td><strong>{r.producto}</strong></td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{(r.fecha || '').split(' ')[0]}</td>
                      <td style={{ fontSize: 12, color: r.estado === 'Por vencer' ? 'var(--amber)' : r.estado === 'Vencido' ? 'var(--red)' : 'var(--text2)' }}>{r.vence || '—'}</td>
                      <td>{r.qty} {r.unidad}</td>
                      <td>{r.proveedor}</td>
                      <td>{estadoBadge(r.estado)}</td>
                      <td><button className="btn btn-ghost btn-xs" onClick={() => setEtiqueta(r)}>🖨 Etiqueta</button></td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal open={!!etiqueta} onClose={() => setEtiqueta(null)} title="Etiqueta de lote">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <LabelPreview rec={etiqueta} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={() => window.print()}>🖨 Imprimir</button>
            <button className="btn btn-ghost" onClick={() => setEtiqueta(null)}>Cerrar</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ============ PEDIDOS ============
export function Pedidos({ showNotif }) {
  const [lotes, setLotes] = useState([]);
  const [carrito, setCarrito] = useState({}); // key = lote code
  const [obs, setObs] = useState('');
  const [pedidos, setPedidos] = useState([]);
  const [search, setSearch] = useState('');

  const loadLotes = async () => {
    const data = await api.getLotes();
    setLotes(data.filter(l => l.condicion !== 'rej'));
  };
  const loadPedidos = async () => { const d = await api.getPedidos(); setPedidos(d); };

  useEffect(() => { loadLotes(); loadPedidos(); }, []);

  const cambiarQty = (loteCode, delta) => {
    const ins = lotes.find(l => l.lote === loteCode);
    if (!ins) return;
    const curr = carrito[loteCode] || 0;
    const nv = Math.max(0, curr + delta);
    if (nv > ins.qtyDisponible) { showNotif(`Stock insuficiente en lote ${loteCode}`); return; }
    const c = { ...carrito };
    if (nv === 0) delete c[loteCode]; else c[loteCode] = nv;
    setCarrito(c);
  };

  const enviar = async () => {
    const keys = Object.keys(carrito);
    if (!keys.length) { showNotif('Agrega al menos un insumo'); return; }
    const items = keys.map(loteCode => {
      const ins = lotes.find(l => l.lote === loteCode);
      return { nombre: ins.producto, qty: carrito[loteCode], unidad: ins.unidad, lote: loteCode };
    });
    try {
      await api.crearPedido({ items, obs });
      showNotif('Pedido enviado a bodega exitosamente');
      setCarrito({}); setObs('');
      loadLotes(); loadPedidos();
    } catch (e) { showNotif('Error: ' + e.message); }
  };

  const aprobar = async (id) => {
    await api.actualizarPedido(id, 'En preparación');
    showNotif('Pedido ' + id + ' aprobado');
    loadPedidos();
  };

  const filtered = lotes.filter(l => !search || l.producto.toLowerCase().includes(search.toLowerCase()));
  const carritoKeys = Object.keys(carrito);

  const estadoBadge = (est) => ({
    'Recepcionado': <span className="badge badge-green">✓ Recepcionado</span>,
    'En preparación': <span className="badge badge-blue">⟳ Preparando</span>,
    'Pendiente': <span className="badge badge-amber">⏳ Pendiente</span>,
  }[est] || <span className="badge badge-gray">{est}</span>);

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Pedido de insumos — Cocina</div>
          <div className="topbar-meta">Selecciona el lote y la cantidad que necesitas</div>
        </div>
      </div>
      <StepBanner
        step={4} title="Pedido a bodega"
        desc="Selecciona el lote y la cantidad que necesitas. El stock disponible se calcula en tiempo real. Una vez aprobado por bodega, pasa a preparación."
        prev="Stock disponible" next="Recepción cocina"
      />
      <div className="page-content">
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div className="card">
            <div className="card-title"><div className="ct-icon">📦</div> Lotes disponibles en bodega</div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar producto..." style={{ width: '100%', marginBottom: 12 }} />
            {filtered.length === 0 && (
              <div className="empty-state"><div className="es-icon">📦</div><div className="es-text">Sin lotes disponibles</div></div>
            )}
            {filtered.map(ins => (
              <div key={ins.lote} className={`insumo-card ${ins.qtyDisponible === 0 ? 'sin-stock' : ''}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                      <span className="lot-tag" style={{ fontSize: 10.5 }}>{ins.lote}</span>
                      {ins.condicion === 'obs' && <span className="badge badge-amber" style={{ fontSize: 10 }}>⚠ Obs</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{ins.producto}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
                      Disponible: <strong style={{ color: ins.qtyDisponible > 0 ? 'var(--green)' : 'var(--text3)' }}>
                        {ins.qtyDisponible} {ins.unidad}
                      </strong>
                      {ins.qtyEnDespacho > 0 && <span style={{ marginLeft: 6, color: 'var(--amber)' }}>· {ins.qtyEnDespacho} en despacho</span>}
                    </div>
                  </div>
                  <div className="qty-control">
                    <button className="qty-btn" onClick={() => cambiarQty(ins.lote, -1)}>−</button>
                    <span className="qty-value">{carrito[ins.lote] || 0}</span>
                    <button className="qty-btn" onClick={() => cambiarQty(ins.lote, 1)}>+</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ position: 'sticky', top: 76 }}>
            <div className="card-title"><div className="ct-icon">🛒</div> Pedido actual</div>
            {carritoKeys.length === 0
              ? <div className="empty-state"><div className="es-icon">🛒</div><div className="es-text">Agrega insumos desde el panel izquierdo</div></div>
              : <div style={{ marginBottom: 12 }}>
                  {carritoKeys.map(loteCode => {
                    const ins = lotes.find(l => l.lote === loteCode);
                    if (!ins) return null;
                    return (
                      <div key={loteCode} style={{ padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600 }}>{ins.producto}</span>
                          <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{carrito[loteCode]} {ins.unidad}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                          Lote: <span className="lot-tag" style={{ fontSize: 10 }}>{loteCode}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
            <hr />
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Observaciones</label>
              <textarea value={obs} onChange={e => setObs(e.target.value)} rows="2" placeholder="Ej: urgente para servicio de almuerzo..." />
            </div>
            <button className="btn btn-primary btn-full" onClick={enviar}>→ Enviar pedido a bodega</button>
          </div>
        </div>

        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-title"><div className="ct-icon">📋</div> Historial de pedidos enviados</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>N° Pedido</th><th>Insumos y lotes</th><th>Enviado</th><th>Estado</th><th>Acción</th></tr></thead>
              <tbody>
                {pedidos.length === 0
                  ? <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text3)', padding: 24 }}>Sin pedidos</td></tr>
                  : pedidos.map(p => (
                    <tr key={p.id}>
                      <td><span className="lot-tag">{p.id}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text2)', maxWidth: 260 }}>
                        {p.items.map((i, idx) => (
                          <span key={idx}>
                            {i.nombre} {i.qty} {i.unidad}
                            {i.lote && <span className="lot-tag" style={{ fontSize: 10, marginLeft: 4 }}>{i.lote}</span>}
                            {idx < p.items.length - 1 && <span style={{ margin: '0 4px', color: 'var(--text3)' }}>·</span>}
                          </span>
                        ))}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{p.fecha}</td>
                      <td>{estadoBadge(p.estado)}</td>
                      <td>{p.estado === 'Pendiente' && <button className="btn btn-ghost btn-xs" onClick={() => aprobar(p.id)}>Aprobar</button>}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ============ RECEPCIÓN COCINA ============
export function RecepcionCocina({ showNotif }) {
  const [pedidos, setPedidos] = useState([]);

  const load = async () => { const d = await api.getPedidos(); setPedidos(d.filter(p => p.estado === 'En preparación')); };
  useEffect(() => { load(); }, []);

  const confirmar = async (id) => {
    await api.actualizarPedido(id, 'Recepcionado', 'Chef C. Gómez');
    showNotif('Recepción confirmada · ' + id);
    load();
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Recepción en cocina</div>
          <div className="topbar-meta">Confirma los insumos recibidos desde bodega</div>
        </div>
      </div>
      <StepBanner
        step={5} title="Recepción cocina"
        desc="El chef confirma que recibió los insumos. Esta acción cierra el ciclo del lote y queda registrado en el historial de trazabilidad."
        prev="Pedido a bodega"
      />
      <div className="page-content">
        <div className="card">
          <div className="card-title"><div className="ct-icon">✓</div> Pedidos listos para confirmar recepción</div>
          {pedidos.length === 0
            ? <div className="empty-state"><div className="es-icon">✓</div><div className="es-text">No hay pedidos pendientes de recepción en cocina</div></div>
            : pedidos.map(p => (
              <div key={p.id} className="pedido-item">
                <div className="pedido-header">
                  <div>
                    <span className="lot-tag">{p.id}</span>
                    <span style={{ marginLeft: 10, fontWeight: 600 }}>{p.items.map(i => `${i.nombre} ${i.qty} ${i.unidad}`).join(' · ')}</span>
                    {p.obs && <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>Obs: {p.obs}</div>}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => confirmar(p.id)}>✓ Confirmar recepción</button>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 8 }}>Enviado: {p.fecha}</div>
              </div>
            ))
          }
        </div>
      </div>
    </>
  );
}

// ============ MAESTRO SAP ============
export function Maestro({ showNotif }) {
  const [productos, setProductos] = useState([]);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [syncing, setSyncing] = useState(false);

  const CATEGORIAS = ['Verduras y Hortalizas', 'Aceites y Grasas', 'Cereales y Harinas', 'Azúcares', 'Condimentos', 'Legumbres'];

  const load = async () => {
    const data = await api.getProductos(search, categoria);
    setProductos(data);
  };
  useEffect(() => { load(); }, [search, categoria]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.syncSAP();
      showNotif(`SAP sincronizado · ${r.count} materiales actualizados`);
      load();
    } catch (e) { showNotif('Error sync SAP: ' + e.message); }
    finally { setSyncing(false); }
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Maestro de materiales SAP</div>
          <div className="topbar-meta">Catálogo de materiales sincronizado desde SAP S/4HANA</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-primary btn-sm" onClick={sync} disabled={syncing}>
            {syncing ? '⟳ Sincronizando...' : '⟳ Sincronizar SAP'}
          </button>
        </div>
      </div>
      <div className="page-content">
        <div className="filter-bar">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por código, descripción o tipo..." />
          <select value={categoria} onChange={e => setCategoria(e.target.value)} style={{ width: 220 }}>
            <option value="">Todas las categorías</option>
            {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="card">
          <div className="section-header">
            <div className="section-title">Materiales SAP</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>{productos.length} materiales</div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Código SAP</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Categoría</th>
                  <th>Unidad</th>
                  <th>Stock mínimo</th>
                  <th>Proveedor preferido</th>
                  <th>Última sync</th>
                </tr>
              </thead>
              <tbody>
                {productos.length === 0
                  ? <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Sin materiales</td></tr>
                  : productos.map(p => (
                    <tr key={p.codigo}>
                      <td><span className="lot-tag">{p.codigo}</span></td>
                      <td><span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: 'var(--text2)' }}>{p.tipo}</span></td>
                      <td><strong>{p.descripcion}</strong></td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.categoria}</td>
                      <td style={{ fontSize: 12 }}>{p.unidad}</td>
                      <td style={{ fontSize: 12 }}>{p.stockMin} {p.unidad}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.proveedor}</td>
                      <td style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{p.ultimaSync}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ============ STOCK ACTUAL ============
export function Stock() {
  const [stock, setStock] = useState([]);

  useEffect(() => { api.getStock().then(setStock).catch(console.error); }, []);

  const getEstado = (s) => {
    if (s.stockActual === 0) return 'Sin stock';
    if (s.stockActual < s.stockMin) return 'Bajo mínimo';
    if (s.stockActual < s.stockMin * 1.3) return 'Por agotarse';
    return 'Normal';
  };

  const estadoBadge = (est) => ({
    'Normal': <span className="badge badge-green">✓ Normal</span>,
    'Por agotarse': <span className="badge badge-amber">⚠ Por agotarse</span>,
    'Bajo mínimo': <span className="badge badge-red">↓ Bajo mínimo</span>,
    'Sin stock': <span className="badge badge-gray">— Sin stock</span>,
  }[est]);

  const totales = {
    normal: stock.filter(s => getEstado(s) === 'Normal').length,
    bajos: stock.filter(s => ['Bajo mínimo', 'Por agotarse'].includes(getEstado(s))).length,
    sinStock: stock.filter(s => getEstado(s) === 'Sin stock').length,
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Stock disponible</div>
          <div className="topbar-meta">Inventario en tiempo real — calculado desde recepciones y despachos</div>
        </div>
      </div>
      <StepBanner
        step={3} title="Stock disponible"
        desc="Muestra cuánto hay disponible de cada lote. Se calcula automáticamente: total recibido menos lo solicitado, en despacho y entregado."
        prev="Lotes en bodega" next="Pedido a bodega"
      />
      <div className="page-content">
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
          <div className="stat-card stat-green">
            <div className="stat-label">Con stock normal</div>
            <div className="stat-value">{totales.normal}</div>
            <div className="stat-sub">Sobre el mínimo</div>
          </div>
          <div className="stat-card stat-amber">
            <div className="stat-label">Niveles bajos</div>
            <div className="stat-value">{totales.bajos}</div>
            <div className="stat-sub">Por agotarse o bajo mínimo</div>
          </div>
          <div className="stat-card stat-primary">
            <div className="stat-label">Sin stock</div>
            <div className="stat-value">{totales.sinStock}</div>
            <div className="stat-sub">Requieren reposición</div>
          </div>
        </div>
        <div className="card">
          <div className="card-title"><div className="ct-icon">📦</div> Inventario por material</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Código SAP</th>
                  <th>Material</th>
                  <th>Categoría</th>
                  <th>Lote activo</th>
                  <th>Ingresado</th>
                  <th>Despachado</th>
                  <th>Stock actual</th>
                  <th>Mínimo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {stock.map(s => {
                  const est = getEstado(s);
                  return (
                    <tr key={s.tipo}>
                      <td><span className="lot-tag">{s.codigo}</span></td>
                      <td><strong>{s.descripcion}</strong></td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{s.categoria}</td>
                      <td>{s.loteActivo !== '—' ? <span className="lot-tag">{s.loteActivo}</span> : '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{s.ingresado} {s.unidad}</td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{s.despachado} {s.unidad}</td>
                      <td>
                        <strong style={{
                          color: est === 'Normal' ? 'var(--green)' : est === 'Sin stock' ? 'var(--text3)' : 'var(--red)',
                          fontSize: 14
                        }}>
                          {s.stockActual} {s.unidad}
                        </strong>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{s.stockMin} {s.unidad}</td>
                      <td>{estadoBadge(est)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ============ HISTORIAL ============
export function Historial() {
  const [eventos, setEventos] = useState([]);
  const [search, setSearch] = useState('');
  const [titulo, setTitulo] = useState('Todos los eventos registrados');

  const load = async (q = '') => {
    const data = await api.getEventos(q);
    setEventos(data);
    setTitulo(q ? 'Trazabilidad: ' + q : 'Todos los eventos registrados');
  };
  useEffect(() => { load(); }, []);

  const eventoBadge = (ev) => ({
    'Recepción': <span className="badge badge-green">Recepción</span>,
    'Despacho cocina': <span className="badge badge-blue">Pedido / Despacho</span>,
    'Pedido cocina': <span className="badge badge-blue">Pedido / Despacho</span>,
    'Recepción cocina': <span className="badge badge-primary">Recep. cocina</span>,
    'Alerta vencimiento': <span className="badge badge-amber">Alerta</span>,
    'Sync SAP': <span className="badge badge-gray">SAP Sync</span>,
  }[ev] || <span className="badge badge-gray">{ev}</span>);

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Historial de trazabilidad</div>
          <div className="topbar-meta">Registro completo de eventos por lote — desde recepción hasta cocina</div>
        </div>
      </div>
      <div style={{ background: 'var(--blue-bg)', borderBottom: '1px solid var(--blue-border)', padding: '9px 28px', fontSize: 12.5, color: 'var(--blue)' }}>
        <strong>📑 Trazabilidad:</strong> Ingresa el código de lote (Ej: <code>TOM-001-A</code>) o el nombre del producto para ver todos los eventos registrados — recepciones, despachos y entregas.
      </div>
      <div className="page-content">
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-title"><div className="ct-icon">🔍</div> Buscar por lote o producto</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(search)} placeholder="Ingresa código de lote (Ej: TOM-001-A) o escanea con pistola lectora..." style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={() => load(search)}>Buscar trazabilidad</button>
            <button className="btn btn-ghost" onClick={() => { setSearch(''); load(''); }}>Ver todo</button>
          </div>
        </div>
        <div className="card">
          <div className="section-header">
            <div className="section-title">{titulo}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>{eventos.length} evento{eventos.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Fecha y hora</th><th>Lote</th><th>Producto</th><th>Evento</th><th>Detalle</th><th>Responsable</th></tr></thead>
              <tbody>
                {eventos.length === 0
                  ? <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Sin eventos para este lote o producto</td></tr>
                  : eventos.map((e, i) => (
                    <tr key={e.id || i}>
                      <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{e.ts}</td>
                      <td>{e.lote !== '—' ? <span className="lot-tag">{e.lote}</span> : '—'}</td>
                      <td style={{ fontWeight: 600 }}>{e.producto}</td>
                      <td>{eventoBadge(e.evento)}</td>
                      <td style={{ fontSize: 12.5, color: 'var(--text2)' }}>{e.detalle}</td>
                      <td style={{ fontSize: 12.5 }}>{e.responsable}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
